import React, { memo, useState, useCallback, useMemo, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
// import { useQuery } from '@apollo/react-hooks'
import Avatar from '@material-ui/core/Avatar'
import CircularProgress from '@material-ui/core/CircularProgress'
import UserAvatar from '@material-ui/icons/AccountCircleOutlined'
import TeamIcon from '@material-ui/icons/PeopleOutline'

// import DefaultLogo from 'assets/images/logo.png'
import styles from './styles'
import {
	buildCompanyLogo,
	buildUserAvatar,
	buildTeamLogo /*capitalize*/,
	s3BucketURL,
} from 'utils'
import { StoreValue } from 'store'
import { UploadCrop } from 'components'
import { EEntity, ICompany, IPublicCompany, ITeam, IUser } from 'models'
import classNames from 'classnames'
import IconButton from '@material-ui/core/IconButton'
import AddAPhoto from '@material-ui/icons/AddAPhotoOutlined'

// import UserAvatar from '@material-ui/icons/AccountCircleOutlined'
// import CompanyLogo from 'assets/images/company-generic-logo.png'

const useStyles = makeStyles(styles)

const EntityLogo: React.FunctionComponent<{
	type: EEntity
	alt?: string
	company?: Pick<ICompany, 'id' | 'logoPath' | 'hasLogo' | 'logoPath'>
	publicCompany?: Pick<
		IPublicCompany,
		'id' | 'avatar_path' | 'original_avatar_path'
	>
	team?: Pick<ITeam, 'id' | 'logoPath' | 'hasLogo'>
	user?: Pick<IUser, 'id' | 'profileCollection'>
	extraAvatarClasses?: string
	disabledEdit?: boolean
	fullView?: boolean
	onAdd?(file: File): void
	isAvatarUploaded?: boolean
	handleAvatarUploadedState?(): void
	showUploadCrop?: boolean
}> = ({
	type,
	alt,
	company,
	publicCompany,
	team,
	user,
	extraAvatarClasses,
	fullView,
	disabledEdit = true,
	onAdd = null,
	isAvatarUploaded = false,
	handleAvatarUploadedState,
	showUploadCrop = false,
}) => {
	const classes = useStyles({})
	// const { data }: any = useQuery(UserQuery, {
	// 	skip: !user,
	// 	variables: {
	// 		id: user && user.id,
	// 	},
	// })
	// const userProfile = (data && data.user) || null
	const [percent, setPercent] = useState<number>(0)
	const [show, setShow] = useState(false)
	if (percent === 100 && onAdd) {
		handleAvatarUploadedState?.()
	}

	const toggleShow = useCallback(() => {
		if (!disabledEdit) setShow(show => !show)
	}, [disabledEdit])

	const avatarSrc = useMemo(() => {
		switch (type) {
			// case EEntity.person:
			// 	return UserAvatar
			case EEntity.company:
				return onAdd && !isAvatarUploaded
					? null
					: company?.id
					? buildCompanyLogo(
							(company && company.id) || '',
							(company && company.hasLogo && company.logoPath) ||
								'company-logo-original.jpg',
					  )
					: publicCompany?.id
					? `${s3BucketURL}/${publicCompany.original_avatar_path}`
					: company?.logoPath ?? null
			case EEntity.team:
				return team && team.id && team.hasLogo
					? buildTeamLogo(
							(team && team.id) || '',
							(team && team.hasLogo && team.logoPath) ||
								'team-logo-original.jpg',
					  )
					: null
			default:
				return undefined
		}
	}, [type, company, team, onAdd, isAvatarUploaded, publicCompany])
	const updatePercent = useCallback(value => {
		const parsedValue = parseInt(value, 10)
		setPercent(parsedValue)
	}, [])
	const avatarPath = useMemo(() => {
		const rawPath =
			(type === EEntity.company && avatarSrc) ||
			(type === EEntity.team && avatarSrc) ||
			(type === EEntity.person && user
				? user.profileCollection?.edges?.[0].node.hasAvatar &&
				  buildUserAvatar(
						user.id,
						user.profileCollection?.edges?.[0].node.avatarPath ??
							'',
				  )
				: '') ||
			undefined

		// Clean up duplicate path segments (e.g., /companies/.../companies/... -> /companies/...)
		if (typeof rawPath === 'string' && rawPath.includes('//')) {
			const urlParts = rawPath.split('/')
			const folders = [
				'companies',
				'teams',
				'users',
				'jobs',
				'articles',
				'stories',
			]

			for (const folder of folders) {
				const firstIndex = urlParts.indexOf(folder)
				const secondIndex = urlParts.indexOf(folder, firstIndex + 1)

				if (firstIndex !== -1 && secondIndex !== -1) {
					// Found duplicate folder, keep base URL + everything from second occurrence
					const baseUrl = urlParts.slice(0, 3).join('/') // https://domain
					const cleanPath = urlParts.slice(secondIndex).join('/')
					return `${baseUrl}/${cleanPath}`
				}
			}
		}

		return rawPath
	}, [type, avatarSrc, user])

	useEffect(() => {
		if (showUploadCrop) {
			toggleShow()
		}
	}, [showUploadCrop, toggleShow])

	return (
		<>
			{Boolean(percent) && (
				<div
					className={classNames([
						fullView
							? classes.avatarFullViewContainer
							: classes.avatarContainer,
						extraAvatarClasses,
					])}
				>
					<CircularProgress
						thickness={1.5}
						variant="static"
						value={percent || 0}
						classes={{
							root: classes.loader,
						}}
					/>
					<span style={{ position: 'absolute' }}>{percent}</span>
				</div>
			)}
			{!Boolean(percent) &&
				(onAdd && !isAvatarUploaded ? (
					<IconButton
						onClick={toggleShow}
						className={classes.uploadBtn}
						aria-label="Add company foto"
					>
						<AddAPhoto fontSize="default" />
					</IconButton>
				) : (
					<Avatar
						alt={alt}
						src={avatarPath}
						onClick={toggleShow}
						className={classNames([
							fullView
								? classes.avatarFullViewContainer
								: classes.avatarContainer,
							extraAvatarClasses,
						])}
						imgProps={{
							style:
								type === EEntity.company ||
								type === EEntity.team // TODO: Talk with Rares about this line
									? {
											objectFit: 'contain',
											backgroundColor: 'rgba(0, 0, 0, 0)',
									  }
									: undefined,
							onError: e => {
								;(
									e.currentTarget as HTMLImageElement
								).src = `${s3BucketURL}/${company?.logoPath}`
							},
						}}
					>
						{type === EEntity.person &&
						(!user ||
							!user.profileCollection?.edges?.[0].node
								.hasAvatar) ? (
							<UserAvatar />
						) : null}
						{type === EEntity.team && (!team || !team.hasLogo) ? (
							<TeamIcon />
						) : null}
					</Avatar>
				))}
			{!disabledEdit && (
				<UploadCrop
					open={show}
					handleClose={toggleShow}
					updatePercent={updatePercent}
					type={type}
					user={user}
					company={company}
					team={team}
					onAdd={onAdd}
				/>
			)}
		</>
	)
}

export default memo((props: any) => (
	<StoreValue keys={['user|storeUser']}>
		<EntityLogo {...props} />
	</StoreValue>
))
